import React from 'react';
import PropTypes from 'prop-types';
import { Button, InputTextArea, SparkIcon } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import { HELP_TEXTS } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

export const EXAMPLES_PLACEHOLDER = 'e.g., "This is a great beautiful movie"';

/** Compact default; the field is vertically resizable from here. */
const EXAMPLE_TEXTAREA_HEIGHT = 48;

export const trimmedExamples = (examples) =>
    (examples || []).map((text) => (text || '').trim()).filter(Boolean);

const asExampleList = (examples) => (examples?.length ? examples : ['']);

/**
 * Per-label example texts: one full-width textarea per example and an
 * "+ Add Example" control so the user can supply several reference samples.
 */
const ExamplesListField = ({ examples, onChange, error = false, readOnly = false }) => {
    const values = asExampleList(examples);

    const patchAt = (index, value) => {
        const next = [...values];
        next[index] = value;
        onChange(next);
    };

    return (
        <div className="gdai-field gdai-examples">
            <FieldLabel text="Examples" required help={HELP_TEXTS.examplesPrompt} />
            <div className="gdai-examples__list">
                {values.map((value, index) => {
                    const rowError = error && !value.trim();
                    return (
                        <div
                            key={`example-${index}`}
                            className={`gdai-examples__row${rowError ? ' gdai-field--error' : ''}`}
                        >
                            <InputTextArea
                                className="gdai-textarea"
                                showLabel={false}
                                size="medium"
                                placeholder={EXAMPLES_PLACEHOLDER}
                                value={value}
                                readOnly={readOnly}
                                aria-invalid={rowError || undefined}
                                style={{ '--gdai-textarea-height': `${EXAMPLE_TEXTAREA_HEIGHT}px` }}
                                onChange={(event) => patchAt(index, event.target.value)}
                            />
                            {!readOnly && values.length > 1 ? (
                                <button
                                    type="button"
                                    className="gdai-examples__remove"
                                    aria-label={`Remove example ${index + 1}`}
                                    onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
                                >
                                    <SparkIcon slug="trash" size="small" />
                                </button>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {readOnly ? null : (
                <div>
                    <Button
                        variant="secondary"
                        size="medium"
                        startIcon={<SparkIcon slug="plus" size="medium" />}
                        onClick={() => onChange([...values, ''])}
                    >
                        Add Example
                    </Button>
                </div>
            )}
        </div>
    );
};

ExamplesListField.propTypes = {
    examples: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.bool,
    readOnly: PropTypes.bool,
};

export default ExamplesListField;
